import type { LineItem } from "@/apis/gql/types";
import debounce from "debounce";
import { pick } from "lodash";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useSyncLineItems } from "@/queries/line-items/sync";

import {
  type OperationAction,
  type OperationActionCreate,
  type OperationActionDelete,
  type OperationActionUpdate,
  OperationType,
} from "./types";

type Props = {
  invoiceId: string;
  operations: OperationAction[];
  onSuccess?: (
    lineItems: LineItem[],
    operations: OperationAction[],
  ) => Promise<void> | void;
  onLoading?: (loading: boolean) => void;
};

const DEBOUNCE_TIME = 500;

export const BackendSync = ({
  invoiceId,
  operations,
  onSuccess,
  onLoading,
}: Props) => {
  const { t } = useTranslation();
  const isLoadingRef = useRef(false);

  const [syncLineItems] = useSyncLineItems({
    invoiceId,
  });

  const debouncedSync = useCallback(
    debounce(async (operations: OperationAction[]) => {
      if (operations.length > 0 && !isLoadingRef.current) {
        const toastId = toast.loading(t("generic_loading"));
        isLoadingRef.current = true;

        const groupedByChangeType = operations.reduce(
          (acc, item) => {
            if (!acc[item.type]) {
              acc[item.type] = [];
            }
            acc[item.type].push(item);
            return acc;
          },
          {} as Record<OperationType, OperationAction[]>,
        );

        const createdLineItems = (groupedByChangeType[OperationType.CREATE] ??
          []) as OperationActionCreate[];
        const updatedLineItems = (groupedByChangeType[OperationType.UPDATE] ??
          []) as OperationActionUpdate[];
        const deletedLineItems = (groupedByChangeType[OperationType.DELETE] ??
          []) as OperationActionDelete[];

        let lineItemsToUpdate: {
          lineItemIds: string[];
          changes: Partial<LineItem>;
        }[] = [];

        let lineItemsToDelete: string[] = [];
        let lineItemsToCreate: Partial<LineItem>[] = [];

        if (updatedLineItems.length > 0) {
          const groupedChanges = updatedLineItems.reduce(
            (acc, curr) => {
              const changeKey = JSON.stringify(curr.changes);

              if (!acc[changeKey]) {
                acc[changeKey] = {
                  lineItemIds: [],
                  changes: curr.changes,
                };
              }

              acc[changeKey].lineItemIds.push(curr.lineItemId);
              return acc;
            },
            {} as Record<
              string,
              { lineItemIds: string[]; changes: Partial<LineItem> }
            >,
          );

          // Convert back to array format
          lineItemsToUpdate = Object.values(groupedChanges);
        }

        if (deletedLineItems.length > 0) {
          lineItemsToDelete = deletedLineItems.map((item) => item.lineItemId);
        }

        if (createdLineItems.length > 0) {
          lineItemsToCreate = createdLineItems.map(({ lineItem }) => {
            const item: any = pick(lineItem, [
              "lineItemId",
              "invoiceId",
              "amount",
              "vatRate",
              "accountId",
              "taxCodeId",
              "description",
              "costCenterId",
              "bookingText",
              "bookingTextManuallyUpdated",
              "accountConfidence",
              "taxCodeConfidence",
              "name",
            ]);

            return item;
          });
        }

        try {
          if (
            lineItemsToUpdate.length > 0 ||
            lineItemsToDelete.length > 0 ||
            lineItemsToCreate.length > 0
          ) {
            const lineItems = await syncLineItems(
              {
                update: lineItemsToUpdate,
                del: { lineItemIds: lineItemsToDelete },
                create: { lineItems: lineItemsToCreate },
              },
              {},
            );

            const _lineItems = lineItems.map((lineItem: any) => ({
              ...lineItem,
              ...lineItem.lineItem,
            }));

            onSuccess?.(_lineItems, operations);

            toast.dismiss(toastId);
          }
        } catch (error) {
          toast.error(t("generic_error"), { id: toastId, richColors: true });
        } finally {
          isLoadingRef.current = false;
          onLoading?.(false);
        }
      }
    }, DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    // Show loading state immediately when there are pending operations
    if (operations.length > 0 && !isLoadingRef.current) {
      onLoading?.(true);
    } else if (operations.length === 0 && !isLoadingRef.current) {
      onLoading?.(false);
    }

    debouncedSync(operations);
  }, [operations, debouncedSync, onLoading]);

  return null;
};
